import { get, post } from 'aws-amplify/api';
import { useCallback, useState } from 'react';
import {
    ApiError,
    ApiErrorResponseData,
    DocumentType,
    GetInput,
    PostInput,
    UseRestApiGetRequestResponse,
    UseRestApiPostRequestResponse,
} from './types';

export const useRestApiGetRequest = (config: GetInput): UseRestApiGetRequestResponse => {
    const [data, setData] = useState<DocumentType | undefined>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<UseRestApiGetRequestResponse['error'] | undefined>();

    const sendRequest = useCallback(async () => {
        try {
            if (!loading) setLoading(true);
            if (error) setError(undefined);
            const { body } = await get({ ...config, apiName: import.meta.env.VITE_REST_API_NAME }).response;
            const data = await body.json();
            setData(data);
        } catch (error) {
            setError(error as UseRestApiGetRequestResponse['error']);
        } finally {
            setLoading(false);
        }
    }, [config, error, loading, setData, setError, setLoading]);

    return { data, error, loading, sendRequest };
};

export const useRestApiPostRequest = <TResponse, TRequest extends DocumentType | FormData = DocumentType>(
    config: PostInput
): UseRestApiPostRequestResponse<TResponse, TRequest> => {
    const [data, setData] = useState<TResponse | undefined>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ApiError | undefined>();
    const [status, setStatus] = useState<number | undefined>();

    const sendRequest = useCallback(
        async (requestData?: TRequest) => {
            try {
                setLoading(true);
                setError(undefined);
                setStatus(undefined);
                setData(undefined);

                const response = await post({
                    ...config,
                    apiName: import.meta.env.VITE_REST_API_NAME,
                    options: {
                        ...config.options,
                        body: requestData,
                    },
                }).response;

                const { body, statusCode } = response;
                const responseData = await body.json();
                setStatus(statusCode);
                setData(responseData as TResponse);
                return responseData as TResponse;
            } catch (err) {
                const amplifyError = err as Error & {
                    response?: {
                        statusCode?: number;
                        body?: string;
                    };
                };

                if (amplifyError.response?.statusCode === 401) {
                    //TODO: update URL for real one
                    let errorData: ApiErrorResponseData = {
                        redirectUrl: '/login',
                    };

                    if (amplifyError.response.body) {
                        try {
                            errorData = { ...errorData, ...JSON.parse(amplifyError.response.body) };
                        } catch {
                            console.error('Unable to parse error response body:', amplifyError.response.body);
                        }
                    }

                    setStatus(401);
                    setError(new ApiError('Token expired or unauthorized', { status: 401, data: errorData }));
                    return undefined;
                } else if (amplifyError.response?.statusCode === 403) {
                    setStatus(403);
                    setError(new ApiError(amplifyError.message || 'Forbidden', { status: 403 }));
                    return undefined;
                }

                setStatus(500);
                setError(new ApiError(amplifyError.message || 'Unknown error occurred', { status: 500 }));
                return undefined;
            } finally {
                setLoading(false);
            }
        },
        [config]
    );

    return { data, error, loading, status, sendRequest };
};
